/**
 * Inventario — réplica del backoffice de Skeleton.
 *   - 4 KPI cards: Total SKUs / Bajo Stock / Sin Stock / Total Unidades
 *   - Filtros: Todos / En Stock / Bajo Stock / Sin Stock + sort dropdown + search
 *   - Tabla con: producto+image | variante | sku | cantidad | reservado | disponible
 *     | estado | umbral | acciones (edit/historial/códigos)
 *   - Modal edit stock con motivo + historial inline
 *   - Modal historial standalone
 *   - Modal códigos QR + barcode (generados con qrcode + jsbarcode)
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'qrcode'
import JsBarcode from 'jsbarcode'
import { Card, Input, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import {
  Package, AlertTriangle, XCircle, Warehouse, Search, Pencil, History as HistoryIcon, QrCode, X, Download, ArrowUpDown,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/inventory')({
  component: InventoryPage,
})

interface InventoryItem {
  id: string
  productId: string
  variantId: string | null
  quantity: number
  reservedQuantity: number
  lowStockThreshold: number
  updatedAt: string
  product: { id: string; name: string; sku: string | null; barcode: string | null; images: string[]; barcodeImage: string | null }
  variant: { id: string; name: string; sku: string | null; barcode: string | null; attributes: Record<string, string>; barcodeImage: string | null } | null
}

interface InventoryResponse {
  data: InventoryItem[]
  stats: { totalSKUs: number; lowStock: number; outOfStock: number; totalUnits: number }
}

interface Movement {
  id: string
  previousQuantity: number
  newQuantity: number
  change: number
  reason: string
  note: string | null
  createdAt: string
  userEmail: string | null
}

type Filter = 'all' | 'in' | 'low' | 'out'
type SortBy = 'status_critical' | 'name_asc' | 'qty_desc' | 'qty_asc' | 'available_asc'

const REASONS: Array<{ key: string; label: string }> = [
  { key: 'llevado_a_fisico', label: 'Llevado al punto físico' },
  { key: 'traido_de_fisico', label: 'Traído del punto físico' },
  { key: 'ajuste_conteo', label: 'Ajuste de conteo' },
  { key: 'merma', label: 'Merma / daño' },
  { key: 'otro', label: 'Otro' },
]

function InventoryPage() {
  const { activeTenantId } = useAuth()
  const [filter, setFilter] = useState<Filter>('all')
  const [sortBy, setSortBy] = useState<SortBy>('status_critical')
  const [search, setSearch] = useState('')
  const [editItem, setEditItem] = useState<InventoryItem | null>(null)
  const [historyItem, setHistoryItem] = useState<InventoryItem | null>(null)
  const [codesItem, setCodesItem] = useState<InventoryItem | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => tenantApi.get<InventoryResponse>('/api/v1/inventory?limit=500'),
    enabled: !!activeTenantId,
  })

  const all = data?.data ?? []
  const stats = data?.stats ?? { totalSKUs: 0, lowStock: 0, outOfStock: 0, totalUnits: 0 }

  const filtered = useMemo(() => {
    let list = all
    if (filter === 'in') list = list.filter((i) => i.quantity > 0)
    if (filter === 'low') list = list.filter((i) => i.quantity > 0 && i.quantity <= i.lowStockThreshold)
    if (filter === 'out') list = list.filter((i) => i.quantity === 0)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((i) =>
        i.product.name.toLowerCase().includes(q) ||
        (i.variant?.sku ?? i.product.sku ?? '').toLowerCase().includes(q) ||
        (i.variant?.name ?? '').toLowerCase().includes(q),
      )
    }
    const statusRank = (i: InventoryItem) =>
      i.quantity === 0 ? 0 : (i.quantity <= i.lowStockThreshold ? 1 : 2)
    return [...list].sort((a, b) => {
      switch (sortBy) {
        case 'status_critical': return statusRank(a) - statusRank(b) || a.product.name.localeCompare(b.product.name)
        case 'name_asc': return a.product.name.localeCompare(b.product.name)
        case 'qty_desc': return b.quantity - a.quantity
        case 'qty_asc': return a.quantity - b.quantity
        case 'available_asc': return (a.quantity - a.reservedQuantity) - (b.quantity - b.reservedQuantity)
      }
    })
  }, [all, filter, sortBy, search])

  return (
    <>
      <PageHeader title="Inventario" description="Stock por producto y variante + audit trail de movimientos" />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Total SKUs" value={stats.totalSKUs} icon={Package} color="text-blue-700 dark:text-blue-400" />
          <Kpi label="Bajo Stock" value={stats.lowStock} icon={AlertTriangle} color="text-yellow-700 dark:text-yellow-400" />
          <Kpi label="Sin Stock" value={stats.outOfStock} icon={XCircle} color="text-red-700 dark:text-red-400" />
          <Kpi label="Total Unidades" value={stats.totalUnits} icon={Warehouse} color="text-emerald-700 dark:text-emerald-400" />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {(['all', 'in', 'low', 'out'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition border',
                filter === f ? 'bg-[hsl(var(--muted))] text-foreground border-border' : 'bg-transparent text-muted-foreground border-transparent hover:text-foreground',
              )}
            >
              {f === 'all' ? 'Todos' : f === 'in' ? 'En Stock' : f === 'low' ? 'Bajo Stock' : 'Sin Stock'}
            </button>
          ))}
          <div className="relative">
            <ArrowUpDown className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="h-9 pl-8 pr-3 bg-[hsl(var(--surface))] border border-border rounded-md text-sm hover:border-foreground/30"
            >
              <option value="status_critical">Crítico primero</option>
              <option value="name_asc">Nombre A-Z</option>
              <option value="qty_desc">Mayor cantidad</option>
              <option value="qty_asc">Menor cantidad</option>
              <option value="available_asc">Menor disponible</option>
            </select>
          </div>
          <div className="relative ml-auto max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input type="search" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Cargando inventario...</p>}
        {!isLoading && filtered.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <Warehouse className="size-12 text-muted-foreground mb-3" />
            <p className="text-base font-medium">Sin items {all.length > 0 ? 'en este filtro' : 'todavía'}</p>
            {all.length === 0 && (
              <p className="text-sm text-muted-foreground mt-1">El inventario aparece automáticamente al crear productos.</p>
            )}
          </Card>
        )}

        {filtered.length > 0 && (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Producto</th>
                    <th className="text-left px-4 py-3">Variante</th>
                    <th className="text-left px-4 py-3">SKU</th>
                    <th className="text-right px-4 py-3">Cantidad</th>
                    <th className="text-right px-4 py-3">Reservado</th>
                    <th className="text-right px-4 py-3">Disponible</th>
                    <th className="text-center px-4 py-3">Estado</th>
                    <th className="text-right px-4 py-3">Umbral</th>
                    <th className="text-center px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((it) => (
                    <InventoryRow
                      key={it.id}
                      item={it}
                      onEdit={() => setEditItem(it)}
                      onHistory={() => setHistoryItem(it)}
                      onCodes={() => setCodesItem(it)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {editItem && <EditStockModal item={editItem} onClose={() => setEditItem(null)} />}
      {historyItem && <HistoryModal item={historyItem} onClose={() => setHistoryItem(null)} />}
      {codesItem && <CodesModal item={codesItem} onClose={() => setCodesItem(null)} />}
    </>
  )
}

function Kpi({ label, value, icon: Icon, color }: {
  label: string; value: number; icon: React.ComponentType<{ className?: string }>; color: string
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <Icon className={cn('size-4', color)} />
      </div>
      <p className="text-3xl font-bold tabular-nums">{value.toLocaleString('es-CO')}</p>
    </Card>
  )
}

function InventoryRow({ item, onEdit, onHistory, onCodes }: {
  item: InventoryItem; onEdit: () => void; onHistory: () => void; onCodes: () => void
}) {
  const available = Math.max(0, item.quantity - item.reservedQuantity)
  const sku = item.variant?.sku ?? item.product.sku ?? '—'
  const variantLabel = item.variant?.name ?? '—'

  const status = item.quantity === 0
    ? { label: 'Sin stock', color: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' }
    : item.quantity <= item.lowStockThreshold
      ? { label: 'Bajo stock', color: 'text-yellow-700 dark:text-yellow-400', dot: 'bg-yellow-500' }
      : { label: 'En stock', color: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500' }

  return (
    <tr className="hover:bg-[hsl(var(--surface))]">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {item.product.images[0] ? (
            <img src={item.product.images[0]} alt={item.product.name} className="size-10 rounded object-cover" />
          ) : (
            <div className="size-10 rounded bg-[hsl(var(--muted))] flex items-center justify-center">
              <Package className="size-4 text-muted-foreground" />
            </div>
          )}
          <span className="font-medium">{item.product.name}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground">{variantLabel}</td>
      <td className="px-4 py-3 font-mono text-xs text-muted-foreground uppercase">{sku}</td>
      <td className="px-4 py-3 text-right font-mono tabular-nums">{item.quantity}</td>
      <td className="px-4 py-3 text-right font-mono tabular-nums text-muted-foreground">{item.reservedQuantity}</td>
      <td className="px-4 py-3 text-right font-mono tabular-nums">{available}</td>
      <td className="px-4 py-3 text-center">
        <span className={cn('inline-flex items-center gap-1.5 text-xs', status.color)}>
          <span className={cn('size-2 rounded-full', status.dot)} />
          {status.label}
        </span>
      </td>
      <td className="px-4 py-3 text-right font-mono tabular-nums text-muted-foreground">{item.lowStockThreshold}</td>
      <td className="px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-1">
          <button onClick={onEdit} className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]" title="Editar stock">
            <Pencil className="size-4" />
          </button>
          <button onClick={onHistory} className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]" title="Historial">
            <HistoryIcon className="size-4" />
          </button>
          <button onClick={onCodes} className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]" title="Códigos">
            <QrCode className="size-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}

function EditStockModal({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const qc = useQueryClient()
  const [newQty, setNewQty] = useState(item.quantity)
  const [reason, setReason] = useState(REASONS[0]!.key)
  const [note, setNote] = useState('')

  const { data: history } = useQuery({
    queryKey: ['inventory-movements', item.id],
    queryFn: () => tenantApi.get<{ data: Movement[] }>(`/api/v1/inventory/${item.id}/movements`),
  })

  const save = useMutation({
    mutationFn: () => tenantApi.patch(`/api/v1/inventory/${item.id}`, { quantity: newQty, reason, note: note || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory'] })
      onClose()
    },
  })

  return (
    <ModalShell onClose={onClose} title="Editar stock" maxWidth="max-w-xl">
      <div className="bg-[hsl(var(--surface))] border border-border rounded-md p-4 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {item.product.images[0]
            ? <img src={item.product.images[0]} className="size-10 rounded object-cover" />
            : <div className="size-10 rounded bg-[hsl(var(--muted))]" />}
          <div className="min-w-0">
            <p className="font-semibold truncate">{item.product.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5 font-mono">
              {item.variant?.name && `${item.variant.name} · `}SKU: {item.variant?.sku ?? item.product.sku ?? '—'}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Stock actual</p>
          <p className="text-2xl font-bold tabular-nums">{item.quantity}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm text-foreground">Nueva cantidad</label>
          <Input type="number" min="0" value={newQty} onChange={(e) => setNewQty(parseInt(e.target.value) || 0)} className="mt-1" />
        </div>
        <div>
          <label className="text-sm text-foreground mb-2 block">Motivo del ajuste</label>
          <div className="space-y-2">
            {REASONS.map((r) => (
              <label key={r.key} className={cn(
                'flex items-center gap-3 p-3 rounded-md border cursor-pointer transition',
                reason === r.key ? 'border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/20' : 'border-border hover:border-border',
              )}>
                <input type="radio" name="reason" checked={reason === r.key} onChange={() => setReason(r.key)} />
                <span className="text-sm">{r.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm text-foreground">Nota adicional (opcional)</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Detalles..." className="mt-1" />
        </div>

        <div>
          <p className="text-sm font-medium flex items-center gap-2 mb-2">
            <HistoryIcon className="size-4" /> Historial de este item
          </p>
          <MovementsList movements={history?.data ?? []} compact />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-border">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending || newQty === item.quantity}>
          {save.isPending ? 'Guardando...' : 'Guardar cambio'}
        </Button>
      </div>
    </ModalShell>
  )
}

function HistoryModal({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['inventory-movements', item.id],
    queryFn: () => tenantApi.get<{ data: Movement[] }>(`/api/v1/inventory/${item.id}/movements`),
  })

  return (
    <ModalShell onClose={onClose} title="Historial de cambios" maxWidth="max-w-xl">
      <div className="bg-[hsl(var(--surface))] border border-border rounded-md p-4 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {item.product.images[0]
            ? <img src={item.product.images[0]} className="size-10 rounded object-cover" />
            : <div className="size-10 rounded bg-[hsl(var(--muted))]" />}
          <div className="min-w-0">
            <p className="font-semibold truncate">{item.product.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5 font-mono">
              {item.variant?.name && `${item.variant.name} · `}SKU: {item.variant?.sku ?? item.product.sku ?? '—'}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Stock actual</p>
          <p className="text-2xl font-bold tabular-nums">{item.quantity}</p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-8">Cargando...</p>
      ) : (
        <MovementsList movements={data?.data ?? []} />
      )}

      <div className="flex justify-end pt-4 mt-4 border-t border-border">
        <Button variant="outline" onClick={onClose}>Cerrar</Button>
      </div>
    </ModalShell>
  )
}

function MovementsList({ movements, compact }: { movements: Movement[]; compact?: boolean }) {
  if (movements.length === 0) {
    return (
      <div className={cn(
        'flex flex-col items-center justify-center text-center bg-[hsl(var(--surface))] border border-border rounded-md',
        compact ? 'py-6' : 'py-12',
      )}>
        <HistoryIcon className="size-8 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">{compact ? 'Sin movimientos registrados' : 'Este item aún no tiene movimientos registrados'}</p>
      </div>
    )
  }
  return (
    <div className="space-y-2">
      {movements.map((m) => {
        const reasonLabel = REASONS.find((r) => r.key === m.reason)?.label ?? m.reason
        const isPositive = m.change > 0
        return (
          <div key={m.id} className="bg-[hsl(var(--surface))] border border-border rounded-md p-3">
            <div className="flex items-center justify-between mb-1">
              <span className={cn('text-sm font-mono font-bold', isPositive ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400')}>
                {isPositive ? '+' : ''}{m.change}
              </span>
              <span className="text-xs text-muted-foreground font-mono">
                {m.previousQuantity} → {m.newQuantity}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{reasonLabel}</p>
            {m.note && <p className="text-xs text-muted-foreground mt-1 italic">"{m.note}"</p>}
            <p className="text-xs text-muted-foreground mt-1">
              {new Date(m.createdAt).toLocaleString('es-CO')}
              {m.userEmail && ` · ${m.userEmail}`}
            </p>
          </div>
        )
      })}
    </div>
  )
}

function CodesModal({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const code = item.variant?.barcode ?? item.product.barcode ?? item.variant?.sku ?? item.product.sku ?? item.id
  const label = item.variant?.name ? `${item.product.name} — ${item.variant.name}` : item.product.name
  const barcodeRef = useRef<SVGSVGElement>(null)
  const qrCanvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, code, {
          format: 'CODE128',
          width: 2,
          height: 60,
          displayValue: true,
          fontSize: 14,
          margin: 5,
          background: '#ffffff',
        })
      } catch (e) {
        console.error('Barcode error:', e)
      }
    }
    if (qrCanvasRef.current) {
      QRCode.toCanvas(qrCanvasRef.current, code, { width: 160, margin: 1 }).catch((e) => console.error('QR error:', e))
    }
  }, [code])

  function downloadBarcode() {
    if (!barcodeRef.current) return
    const svg = barcodeRef.current
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const img = new Image()
    img.onload = () => {
      canvas.width = svg.clientWidth || 300
      canvas.height = svg.clientHeight || 100
      const ctx = canvas.getContext('2d')!
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0)
      const a = document.createElement('a')
      a.download = `barcode-${code}.png`
      a.href = canvas.toDataURL('image/png')
      a.click()
    }
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
  }

  function downloadQr() {
    if (!qrCanvasRef.current) return
    const a = document.createElement('a')
    a.download = `qr-${code}.png`
    a.href = qrCanvasRef.current.toDataURL('image/png')
    a.click()
  }

  return (
    <ModalShell onClose={onClose} title={`Códigos — ${label}`} maxWidth="max-w-2xl">
      <p className="text-xs text-muted-foreground mb-4">
        SKU: <code className="bg-[hsl(var(--surface))] px-2 py-0.5 rounded text-foreground font-mono">{code}</code>
      </p>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-medium mb-3">Código de Barras</p>
          <div className="bg-white rounded-md p-4 flex items-center justify-center">
            <svg ref={barcodeRef} />
          </div>
          <Button variant="outline" size="sm" onClick={downloadBarcode} className="w-full mt-2">
            <Download className="size-3.5 mr-2" /> PNG
          </Button>
        </div>

        <div>
          <p className="text-sm font-medium mb-3">Código QR</p>
          <div className="bg-white rounded-md p-4 flex items-center justify-center">
            <canvas ref={qrCanvasRef} />
          </div>
          <Button variant="outline" size="sm" onClick={downloadQr} className="w-full mt-2">
            <Download className="size-3.5 mr-2" /> PNG
          </Button>
        </div>
      </div>

      <div className="flex justify-end pt-4 mt-4 border-t border-border">
        <Button variant="outline" onClick={onClose}>Cerrar</Button>
      </div>
    </ModalShell>
  )
}

function ModalShell({ children, onClose, title, maxWidth = 'max-w-lg' }: {
  children: React.ReactNode; onClose: () => void; title: string; maxWidth?: string
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <Card className={cn('w-full max-h-[90vh] overflow-y-auto', maxWidth)}>
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-[hsl(var(--surface))] z-10">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="size-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </Card>
    </div>
  )
}
